import React, { useState, useRef, useEffect } from 'react'
import parse from 'html-react-parser'
import ReviewStars from '@components/Reviews/ReviewStars.jsx'
import CarouselNav from '@components/Slider/CarouselNav.jsx'
import LeftArrow from '@components/Icons/LeftArrow.jsx'
import RightArrow from '@components/Icons/RightArrow.jsx'

const ReviewsCarousel = ({ reviewsData }) => {
    const [showPrev, setShowPrev] = useState(false)
    const [showNext, setShowNext] = useState(true)
    const [showNavigation, setShowNavigation] = useState(true)
    const carouselRef = useRef(null)
    const [scrollX, setScrollX] = useState(0)
    const [scrollEnd, setScrollEnd] = useState(false)

    useEffect(() => {
        const carousel = carouselRef.current
        if (!carousel) return

        const updateButtonStates = () => {
            if (!carousel) return
            const { scrollLeft, scrollWidth, clientWidth } = carousel
            setShowPrev(scrollLeft > 0)
            setShowNext(scrollLeft < scrollWidth - clientWidth - 1)
        }

        updateButtonStates()
        carousel.addEventListener('scroll', updateButtonStates)

        return () => {
            carousel.removeEventListener('scroll', updateButtonStates)
        }
    }, [])

    const movePrev = () => {
        const carousel = carouselRef?.current;
        if (carousel) {
            let frameWidth = carousel.firstChild.offsetWidth;
            if (window.innerWidth > 800) {
                carousel.scrollLeft -= frameWidth * 3 + 10;
            } else {
                carousel.scrollLeft -= frameWidth * 2 + 10;
            }
            setScrollX(carousel.scrollLeft);
            setScrollEnd(
                Math.floor(carousel.scrollWidth - carousel.scrollLeft) <= carousel.offsetWidth
            );
            // Manually update button states after scroll
            if (typeof carousel.onscroll === 'function') {
                carousel.onscroll();
            } else {
                // fallback: call the updateButtonStates logic directly
                const { scrollLeft, scrollWidth, clientWidth } = carousel;
                setShowPrev(scrollLeft > 0);
                setShowNext(scrollLeft < scrollWidth - clientWidth - 1);
            }
        }
    };

    const moveNext = () => {
        const carousel = carouselRef?.current;
        if (carousel) {
            let frameWidth = carousel.firstChild.offsetWidth;
            if (window.innerWidth > 800) {
                carousel.scrollLeft += frameWidth * 3 + 10;
            } else {
                carousel.scrollLeft += frameWidth * 2 + 10;
            }
            setScrollX(carousel.scrollLeft);
            setScrollEnd(
                Math.floor(carousel.scrollWidth - carousel.scrollLeft) <= carousel.offsetWidth
            );
            // Manually update button states after scroll
            if (typeof carousel.onscroll === 'function') {
                carousel.onscroll();
            } else {
                // fallback: call the updateButtonStates logic directly
                const { scrollLeft, scrollWidth, clientWidth } = carousel;
                setShowPrev(scrollLeft > 0);
                setShowNext(scrollLeft < scrollWidth - clientWidth - 1);
            }
        }
    };

    return (
        <div className="reviews-carousel relative">

            <div className="relative mb-2">
                <div className="carousel-header flex mb-3 items-center lg:justify-end justify-between px-4 lg:px-0">
                    {/* Mobile Title */}
                    <div className="lg:hidden w-full bg-[#F4F0ED]">
                        <h2 className="text-xl uppercase">{parse(reviewsData.title.replace(/<br\s*\/?>/gi, ' '))}</h2>
                    </div>
                    {showNavigation && (
                        <CarouselNav
                            showPrev={showPrev}
                            showNext={showNext}
                            movePrev={movePrev}
                            moveNext={moveNext}
                        />
                    )}
                </div>

                <div
                    ref={carouselRef}
                    className="carousel-container relative grid grid-flow-col auto-cols-[minmax(180px,180px)] lg:auto-cols-[minmax(300px,300px)] gap-4 overflow-x-scroll scroll-smooth z-0"
                    style={{
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                    }}
                >
                    {/* Title Tile */}
                    <div className="carousel-item hidden lg:block w-[180px] lg:w-[300px] flex-shrink-0 sticky left-0 z-20 h-full bg-[#F4F0ED]">
                        <div className="relative z-10 flex flex-col gap-2 p-4 h-full justify-center text-center">
                            <h2 className="text-xl lg:text-2xl xl:text-3xl uppercase">{parse(reviewsData.title)}</h2>
                        </div>
                    </div>

                    {/* Review Tiles */}
                    {reviewsData.tiles.map((review, index) => {
                        return (
                            <div key={index} className="carousel-item w-[180px] lg:w-[300px] flex-shrink-0">
                                <div className="review-tile h-full flex flex-col bg-white">
                                    <a href={review.link} className="review-image mb-4 relative block">
                                        <img
                                            src={review.image}
                                            alt={review.title}
                                            className="w-full h-auto object-cover"
                                        />
                                        <div
                                                className={`mt-2 px-1 lg:px-2 py-0 border border-slate font-light text-[9px] lg:text-xs inline-flex items-center flex-0 bg-white text-flint justify-center text-center z-[2] absolute top-0 -left-[1px]'
                                        }`}
                                            >
                                                <span className="lg:text-[12px] uppercase font-semibold">
                                                    BESTSELLER
                                                </span>
                                            </div>
                                    </a>
                                    <div className="review-content flex flex-col h-full pt-1 pb-4 pl-4 pr-4 text-center">
                                        <a href={review.link} className="text-base mb-2 leading-5 hover:underline">{review.title}</a>
                                        <div className="flex flex-col flex-grow">
                                            <p className="text-xs mb-2 font-normal min-h-[80px]">"{review.review}"</p>
                                        </div>
                                        <div className="review-rating flex items-center gap-2 justify-center mt-auto">
                                            <ReviewStars rating={review.rating.toString()} width="w-4" />
                                            <span className="text-xs text-gray-500">({review.numberOfReviews})</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

export default ReviewsCarousel
